import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { images } from '../data/images';
import './ContactPage.css';

interface FormData {
    name: string;
    email: string;
    phone: string;
    message: string;
}

const ContactPage: React.FC = () => {
    const { translations, isRTL } = useLanguage();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Format WhatsApp message with bold titles
        const whatsappMessage = `*${translations.name}:* ${formData.name}\n*${translations.email}:* ${formData.email}\n*${translations.phone}:* ${formData.phone}\n*${translations.message}:* ${formData.message}`;
        
        // Encode message for URL
        const encodedMessage = encodeURIComponent(whatsappMessage);
        
        // WhatsApp number (without + and spaces)
        const whatsappNumber = '966535339904';
        
        // Create WhatsApp URL
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
        
        // Redirect to WhatsApp
        window.open(whatsappUrl, '_blank');

        setIsSubmitting(false);
        setIsSubmitted(true);

        // Reset form after 3 seconds
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({ name: '', email: '', phone: '', message: '' });
        }, 1000);
    };

    return (
        <div className={`contact-page ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="container">
                {/* Page Header */}
                <div className="page-header">
                    <h1>{translations.contact}</h1>
                    <p className="page-description">
                        {isRTL
                            ? 'نحن هنا لمساعدتك. تواصل معنا للحصول على استشارة مجانية أو لأي استفسارات حول منتجاتنا وخدماتنا.'
                            : 'We are here to help you. Contact us for free consultation or any inquiries about our products and services.'
                        }
                    </p>
                </div>

                <div className="contact-content">
                    {/* Contact Form */}
                    <div className="contact-form-section">
                        <div className="form-header">
                            <h2>{translations.contactForm}</h2>
                            <p>
                                {isRTL
                                    ? 'املأ النموذج أدناه وسنتواصل معك في أقرب وقت ممكن'
                                    : 'Fill out the form below and we will get back to you as soon as possible'
                                }
                            </p>
                        </div>

                        {isSubmitted ? (
                            <div className="success-message">
                                <div className="success-icon">✓</div>
                                <h3>
                                    {isRTL ? 'تم إرسال رسالتك بنجاح!' : 'Message sent successfully!'}
                                </h3>
                                <p>
                                    {isRTL
                                        ? 'شكراً لتواصلك معنا. سنقوم بالرد على رسالتك خلال 24 ساعة.'
                                        : 'Thank you for contacting us. We will respond to your message within 24 hours.'
                                    }
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="contact-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name" className="form-label">
                                            {translations.name} *
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                            placeholder={isRTL ? 'الاسم الكامل' : 'Full Name'}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone" className="form-label">
                                            {translations.phone}
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder={isRTL ? 'رقم الهاتف' : 'Phone Number'}
                                        />
                                    </div>

                                </div>
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">
                                        {translations.email} *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                        placeholder={isRTL ? 'البريد الإلكتروني' : 'Email Address'}
                                    />
                                </div>


                                <div className="form-group">
                                    <label htmlFor="message" className="form-label">
                                        {translations.message} *
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="form-input form-textarea"
                                        required
                                        rows={6}
                                        placeholder={isRTL ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-small"></div>
                                            {isRTL ? 'جاري الإرسال...' : 'Sending...'}
                                        </>
                                    ) : (
                                        <>
                                            <Send size={20} />
                                            {translations.submit}
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="contact-info-section">
                        <h2>
                            {isRTL ? 'معلومات الاتصال' : 'Contact Information'}
                        </h2>

                        <div className="contact-info">
                            <div className="contact-item">
                                <div className="contact-icon">
                                    <Phone size={24} />
                                </div>
                                <div className="contact-details">
                                    <h3>{translations.phone}</h3>
                                    <p>+966535339904</p>
                                    {/* <p>+1 (555) 987-6543</p> */}
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-icon">
                                    <Mail size={24} />
                                </div>
                                <div className="contact-details">
                                    <h3>{translations.email}</h3>
                                    <p>info@dawakendna.com</p>
                                    {/* <p>support@unicarepharma.com</p> */}
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-icon">
                                    <MapPin size={24} />
                                </div>
                                <div className="contact-details">
                                    <h3>
                                        {isRTL ? 'العنوان' : 'Address'}
                                    </h3>
                                    <p>
                                        {isRTL
                                            ? 'الرياض حي اشبيليه اعلى هايبر رامز الدور الثالث'
                                            : 'Riyadh, Ishbiliyah District, Above Ramez Hypermarket, 3rd Floor'
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* <div className="contact-item">
                                <div className="contact-icon">
                                    <Clock size={24} />
                                </div>
                                <div className="contact-details">
                                    <h3>
                                        {isRTL ? 'ساعات العمل' : 'Working Hours'}
                                    </h3>
                                    <p>
                                        {isRTL ? 'الأحد - الخميس: 9:00 ص - 6:00 م' : 'Sun - Thu: 9:00 AM - 6:00 PM'}
                                    </p>
                                    <p>
                                        {isRTL ? 'الجمعة: 9:00 ص - 12:00 م' : 'Friday: 9:00 AM - 12:00 PM'}
                                    </p>
                                    <p>
                                        {isRTL ? 'السبت: مغلق' : 'Saturday: Closed'}
                                    </p>
                                </div>
                            </div> */}
                        </div>

                        {/* Map Placeholder */}
                        <div className="map-section">
                            <h3>
                                {isRTL ? 'موقعنا' : 'Our Location'}
                            </h3>
                            <div className="map-placeholder">
                                <img
                                    src={images.contact}
                                    alt={isRTL ? 'خريطة الموقع' : 'Location Map'}
                                />
                                <div className="map-overlay">
                                    <MapPin size={32} />
                                    <p>{isRTL ? 'دوائك عندنا' : 'Dawak Endna'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;